import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// =============================================================================
// ANIMATED BUTTON WITH STATES
// =============================================================================

type ButtonState = 'idle' | 'loading' | 'success' | 'error';

interface AnimatedButtonProps {
  children: React.ReactNode;
  onClick?: () => Promise<void> | void;
  className?: string;
  style?: React.CSSProperties;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loadingText?: string;
  successText?: string;
  errorText?: string;
  autoReset?: boolean;
  resetDelay?: number;
}

export const AnimatedButton: React.FC<AnimatedButtonProps> = ({
  children,
  onClick,
  className,
  style,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loadingText = 'Loading...',
  successText = 'Success!',
  errorText = 'Error',
  autoReset = true,
  resetDelay = 2000,
}) => {
  const [state, setState] = useState<ButtonState>('idle');

  const handleClick = async () => {
    if (disabled || state !== 'idle' || !onClick) return;

    setState('loading');
    try {
      await onClick();
      setState('success');
    } catch {
      setState('error');
    }

    if (autoReset) {
      setTimeout(() => setState('idle'), resetDelay);
    }
  };

  const sizeStyles = {
    sm: { padding: '8px 16px', fontSize: '0.875rem' },
    md: { padding: '12px 24px', fontSize: '1rem' },
    lg: { padding: '16px 32px', fontSize: '1.125rem' },
  };

  const variantStyles = {
    primary: {
      background: 'var(--accent, #3b82f6)',
      color: 'white',
      border: 'none',
    },
    secondary: {
      background: 'transparent',
      color: 'var(--accent, #3b82f6)',
      border: '2px solid var(--accent, #3b82f6)',
    },
    ghost: {
      background: 'transparent',
      color: 'var(--text, inherit)',
      border: 'none',
    },
  };

  const stateColors = {
    idle: variantStyles[variant].background,
    loading: variantStyles[variant].background,
    success: '#22c55e',
    error: '#ef4444',
  };

  const content = {
    idle: children,
    loading: (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <LoadingSpinner size={size === 'sm' ? 14 : size === 'md' ? 16 : 18} />
        {loadingText}
      </span>
    ),
    success: (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <CheckIcon size={size === 'sm' ? 14 : size === 'md' ? 16 : 18} />
        {successText}
      </span>
    ),
    error: (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <XIcon size={size === 'sm' ? 14 : size === 'md' ? 16 : 18} />
        {errorText}
      </span>
    ),
  };

  return (
    <motion.button
      className={className}
      style={{
        ...sizeStyles[size],
        ...variantStyles[variant],
        borderRadius: 8,
        cursor: disabled || state !== 'idle' ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 500,
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
      onClick={handleClick}
      disabled={disabled || state !== 'idle'}
      whileHover={state === 'idle' && !disabled ? { scale: 1.02, y: -2 } : {}}
      whileTap={state === 'idle' && !disabled ? { scale: 0.98 } : {}}
      animate={{
        background: stateColors[state],
      }}
      transition={{ duration: 0.2 }}
    >
      <AnimatePresence mode="wait">
        <motion.span
          key={state}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.15 }}
        >
          {content[state]}
        </motion.span>
      </AnimatePresence>
    </motion.button>
  );
};

// =============================================================================
// RIPPLE BUTTON
// =============================================================================

interface RippleButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  rippleColor?: string;
  disabled?: boolean;
}

interface Ripple {
  id: number;
  x: number;
  y: number;
  size: number;
}

export const RippleButton: React.FC<RippleButtonProps> = ({
  children,
  onClick,
  className,
  style,
  rippleColor = 'rgba(255, 255, 255, 0.4)',
  disabled = false,
}) => {
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const idCounter = useRef(0);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;

    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 2;
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;

    const newRipple: Ripple = {
      id: idCounter.current++,
      x,
      y,
      size,
    };

    setRipples((prev) => [...prev, newRipple]);

    // Remove ripple after animation
    setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== newRipple.id));
    }, 600);

    onClick?.();
  };

  return (
    <motion.button
      ref={buttonRef}
      className={className}
      style={{
        position: 'relative',
        overflow: 'hidden',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
      onClick={handleClick}
      disabled={disabled}
      whileHover={!disabled ? { scale: 1.02 } : {}}
      whileTap={!disabled ? { scale: 0.98 } : {}}
    >
      {children}

      {/* Ripples */}
      <AnimatePresence>
        {ripples.map((ripple) => (
          <motion.span
            key={ripple.id}
            style={{
              position: 'absolute',
              left: ripple.x,
              top: ripple.y,
              width: ripple.size,
              height: ripple.size,
              borderRadius: '50%',
              background: rippleColor,
              pointerEvents: 'none',
            }}
            initial={{ scale: 0, opacity: 0.8 }}
            animate={{ scale: 1, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          />
        ))}
      </AnimatePresence>
    </motion.button>
  );
};

// =============================================================================
// MAGNETIC BUTTON (Follows cursor)
// =============================================================================

interface MagneticButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  strength?: number;
  disabled?: boolean;
}

export const MagneticButton: React.FC<MagneticButtonProps> = ({
  children,
  onClick,
  className,
  style,
  strength = 0.3,
  disabled = false,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || !buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const deltaX = (e.clientX - centerX) * strength;
    const deltaY = (e.clientY - centerY) * strength;

    setPosition({ x: deltaX, y: deltaY });
  };

  const handleMouseLeave = () => {
    setPosition({ x: 0, y: 0 });
  };

  return (
    <motion.button
      ref={buttonRef}
      className={className}
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
      onClick={onClick}
      disabled={disabled}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      animate={{ x: position.x, y: position.y }}
      transition={{ type: 'spring', stiffness: 150, damping: 15 }}
      whileTap={!disabled ? { scale: 0.95 } : {}}
    >
      {children}
    </motion.button>
  );
};

// =============================================================================
// ICON BUTTON WITH ANIMATION
// =============================================================================

interface IconButtonProps {
  icon: React.ReactNode;
  activeIcon?: React.ReactNode;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  isActive?: boolean;
  tooltip?: string;
  size?: 'sm' | 'md' | 'lg';
}

export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  activeIcon,
  onClick,
  className,
  style,
  isActive = false,
  size = 'md',
}) => {
  const sizeMap = {
    sm: 32,
    md: 40,
    lg: 48,
  };

  return (
    <motion.button
      className={className}
      style={{
        width: sizeMap[size],
        height: sizeMap[size],
        borderRadius: '50%',
        border: 'none',
        background: isActive ? 'var(--accent, #3b82f6)' : 'transparent',
        color: isActive ? 'white' : 'var(--text, inherit)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
      onClick={onClick}
      whileHover={{ scale: 1.1, backgroundColor: 'rgba(255,255,255,0.1)' }}
      whileTap={{ scale: 0.9 }}
    >
      <AnimatePresence mode="wait">
        <motion.span
          key={isActive ? 'active' : 'inactive'}
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          exit={{ scale: 0, rotate: 180 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        >
          {isActive && activeIcon ? activeIcon : icon}
        </motion.span>
      </AnimatePresence>
    </motion.button>
  );
};

// =============================================================================
// EXPANDABLE FAB (Floating Action Button)
// =============================================================================

interface FABAction {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

interface ExpandableFABProps {
  icon: React.ReactNode;
  actions: FABAction[];
  className?: string;
  style?: React.CSSProperties;
}

export const ExpandableFAB: React.FC<ExpandableFABProps> = ({
  icon,
  actions,
  className,
  style,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        ...style,
      }}
    >
      {/* Action buttons */}
      <AnimatePresence>
        {isExpanded &&
          actions.map((action, index) => (
            <motion.button
              key={index}
              initial={{ opacity: 0, y: 20, scale: 0.5 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.5 }}
              transition={{
                delay: index * 0.05,
                type: 'spring',
                stiffness: 300,
                damping: 20,
              }}
              style={{
                position: 'absolute',
                bottom: 60 + index * 56,
                left: 4,
                width: 48,
                height: 48,
                borderRadius: '50%',
                border: 'none',
                background: 'var(--card, #1f2937)',
                color: 'var(--text, white)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              }}
              onClick={() => {
                action.onClick();
                setIsExpanded(false);
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              {action.icon}
            </motion.button>
          ))}
      </AnimatePresence>

      {/* Main FAB */}
      <motion.button
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          border: 'none',
          background: 'var(--accent, #3b82f6)',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(59, 130, 246, 0.4)',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
        animate={{ rotate: isExpanded ? 45 : 0 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {icon}
      </motion.button>
    </div>
  );
};

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

const LoadingSpinner: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    animate={{ rotate: 360 }}
    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </motion.svg>
);

const CheckIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={3}
    initial={{ pathLength: 0 }}
    animate={{ pathLength: 1 }}
    transition={{ duration: 0.3 }}
  >
    <motion.path d="M20 6L9 17l-5-5" />
  </motion.svg>
);

const XIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={3}
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

export default {
  AnimatedButton,
  RippleButton,
  MagneticButton,
  IconButton,
  ExpandableFAB,
};
